import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const NavigationBar = ({ 
  logoSrc = '/assets/images/logo.png', 
  className = '',
  isTransparent = false 
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const navigationItems = [
    { label: 'Home', path: '/home' },
    { label: 'Services', path: '/services' },
    { label: 'About', path: '/about' },
    { label: 'Contact', path: '/contact' },
    { label: 'Get Started', path: '/application', isButton: true }
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setIsMobileMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location?.pathname === path;
  };

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-navigation transition-all duration-300 ${
          isScrolled || !isTransparent 
            ? 'bg-background/90 backdrop-blur-nav border-b border-border' :'bg-transparent'
        } ${className}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link 
              to="/home" 
              className="flex items-center space-x-2 hover-scale focus-ring rounded-lg"
            >
              <div className="w-8 h-8 lg:w-10 lg:h-10 bg-accent rounded-lg flex items-center justify-center">
                <span className="text-accent-foreground font-bold text-lg lg:text-xl">D</span>
              </div>
              <span className="text-foreground font-semibold text-lg lg:text-xl">
                DARAJA.LABS
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {navigationItems?.map((item) => (
                <div key={item?.path}>
                  {item?.isButton ? (
                    <Link
                      to={item?.path}
                      className="bg-accent text-accent-foreground px-6 py-2.5 rounded-lg font-medium hover-scale focus-ring transition-all duration-200 hover:bg-accent/90"
                    >
                      {item?.label}
                    </Link>
                  ) : (
                    <Link
                      to={item?.path}
                      className={`text-sm font-medium transition-all duration-200 hover:text-accent focus-ring rounded-md px-3 py-2 ${
                        isActivePath(item?.path)
                          ? 'text-accent' :'text-text-secondary hover:text-foreground'
                      }`}
                    >
                      {item?.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={handleMobileMenuToggle}
              className="lg:hidden p-2 rounded-lg text-text-secondary hover:text-foreground hover:bg-muted/50 focus-ring transition-colors duration-200"
              aria-label="Toggle mobile menu"
            >
              <Icon 
                name={isMobileMenuOpen ? "X" : "Menu"} 
                size={24} 
                className="transition-transform duration-200" 
              />
            </button>
          </div>
        </div>
      </nav>
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-mobile-menu lg:hidden">
          <div 
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={handleMobileMenuClose}
          />
          <div className="absolute right-0 top-0 h-full w-80 max-w-[85vw] bg-card border-l border-border animate-slide-in-right">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <span className="text-lg font-semibold text-card-foreground">Menu</span>
              <button
                onClick={handleMobileMenuClose}
                className="p-2 rounded-lg text-muted-foreground hover:text-card-foreground hover:bg-muted/50 focus-ring transition-colors duration-200"
                aria-label="Close menu"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {navigationItems?.map((item) => (
                <div key={item?.path}>
                  {item?.isButton ? (
                    <Link
                      to={item?.path}
                      onClick={handleMobileMenuClose}
                      className="block w-full bg-accent text-accent-foreground px-4 py-3 rounded-lg font-medium text-center hover:bg-accent/90 focus-ring transition-colors duration-200"
                    >
                      {item?.label}
                    </Link>
                  ) : (
                    <Link
                      to={item?.path}
                      onClick={handleMobileMenuClose}
                      className={`block px-4 py-3 rounded-lg font-medium transition-colors duration-200 focus-ring ${
                        isActivePath(item?.path)
                          ? 'text-accent bg-accent/10' :'text-card-foreground hover:text-accent hover:bg-muted/50'
                      }`}
                    >
                      {item?.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NavigationBar;